// src\components\Payroll\PayrollSalaryBatchFld\shared\usePartialSalaryBatch.js
import { useState, useCallback, useEffect } from "react";
import axios from '../../../../api/apiClient';
import { domain, token } from '../../../Security';
import { fetchDataForVessel } from "../../../Personnel/EmployeesListFld/EmployeesListApi";
import { IDENTIFIER } from "../../../../utils/enums";

export const usePartialSalaryBatch = ({ user, showGeneralMessage }) => {
  const [loading, setLoading] = useState(false);
  const [dropdownsLoading, setDropdownsLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [isGenerate, setIsGenerate] = useState(false);

  const [options, setOptions] = useState({
    payrollPeriods: [],
    partialSalaries: [],
    activeSchedules: [], // ADDED: active month names from PartialSalarySchedule
  });

  // ── Fetch main table data ─────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${domain}/api/PartialSalaryBatch`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setData(response.data);
      setFilteredData(response.data);
    } catch (error) {
      console.error("Error fetching Partial Salary Batch data:", error);
    } finally {
      setLoading(false);
      setIsGenerate(false);
    }
  }, []);
  console.log({ data });
  useEffect(() => {
    if (isGenerate) {
      fetchData();
    }
  }, [isGenerate, fetchData]);

  // ── Fetch dropdowns ───────────────────────────────────────────────────────
  const fetchPayrollPeriods = async () => {
    const identifier = IDENTIFIER.FIRST;
    const res = await axios.get(`${domain}/api/PayrollPeriod/get-by-identifier`, {
      params: { identifier },
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  };

  const fetchPartialSalaries = async () => {
    const res = await axios.get(`${domain}/api/PartialSalary`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  };

  // ADDED: fetch only active schedules — used for effective date month validation
  const fetchActiveSchedules = async () => {
    const res = await axios.get(`${domain}/api/PartialSalarySchedule/active`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  };

  const fetchDropdowns = useCallback(async () => {
    try {
      setDropdownsLoading(true);

      const [payrollPeriods, partialSalaries, activeSchedules, vessels] = await Promise.all([
        fetchPayrollPeriods(),
        fetchPartialSalaries(),
        fetchActiveSchedules(),
        fetchDataForVessel(),  // ADDED
      ]);

      setOptions({
        payrollPeriods: payrollPeriods.map((p) => ({
          value: p.id,
          label: `${p.name} ${p.payTypeDescription}`,
          identifier: p.identifier || "",
          dateFrom: p.dateFrom || null,
        })),
        partialSalaries: partialSalaries.map((s) => ({
          value: s.id,
          label: `${s.employeeTypeDescription || s.employeeType || ""} — ${s.positionDescription || s.position || ""}`,
          employeeType: s.employeeTypeDescription || s.employeeType || "",
          position: s.positionDescription || s.position || "",
          amount: s.amount ?? 0,
        })),
        // ADDED: store active month names as plain strings for easy lookup in modal
        activeSchedules: activeSchedules.map((s) => s.monthName),
        vessels: vessels.map((v) => ({
          value: v.id,
          label: `${v.code} - ${v.name}`,
          vessel: v.name,
          code: v.code
        }))
      });

      console.log({ payrollPeriods });
    } catch (error) {
      console.error("Dropdown fetch error:", error);
    } finally {
      setDropdownsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDropdowns();
  }, [fetchDropdowns]);

  // ── Add ───────────────────────────────────────────────────────────────────
  const handleAdd = async (form, onSuccess) => {
    try {
      setRequestLoading(true);
      const payload = buildPayload(form, true);
      await axios.post(`${domain}/api/PartialSalaryBatch`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showGeneralMessage("Partial Salary Batch created successfully!", true);
      await fetchData();
      onSuccess?.();
    } catch (error) {
      console.error("Error creating Partial Salary Batch:", error);
      const msg = error?.response?.data?.message || "Error creating Partial Salary Batch.";
      showGeneralMessage(msg, false);
    } finally {
      setRequestLoading(false);
    }
  };

  // ── Update ────────────────────────────────────────────────────────────────
  const handleUpdate = async (form, recordId, onSuccess) => {
    try {
      console.log({update: form});

      setRequestLoading(true);
      const payload = buildPayload(form, false);
      await axios.put(`${domain}/api/PartialSalaryBatch/${recordId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showGeneralMessage("Partial Salary Batch updated successfully!", true);
      await fetchData();
      onSuccess?.();
    } catch (error) {
      console.error("Error updating Partial Salary Batch:", error);
      const msg = error?.response?.data?.message || "Error updating Partial Salary Batch.";
      showGeneralMessage(msg, false);
    } finally {
      setRequestLoading(false);
    }
  };

  // ── Search filter ─────────────────────────────────────────────────────────
  const handleFilter = useCallback(
    (value) => {
      if (!value) {
        setData(filteredData);
        return;
      }
      const lower = value.toLowerCase();
      const res = filteredData.filter(
        (f) =>
          f?.payrollPeriodDescription?.toLowerCase().includes(lower) ||
          f?.status?.toLowerCase().includes(lower)
      );
      setData(res);
    },
    [filteredData]
  );

  // ── Payload builder — REMOVED: months no longer sent ─────────────────────
  const buildPayload = (form, isAdd) => ({
    payrollPeriodId: form.payrollPeriodId ? parseInt(form.payrollPeriodId) : null,
    vesselId: form.vesselId ? parseInt(form.vesselId) : null, // ADDED
    effectiveDate: form.effectiveDate || null,
    status: form.status || null,
    partialSalaryBatchDetails: (form.partialSalaryBatchDetails || []).map((r) => ({
      id: r.id || 0,
      partialSalaryId: r.partialSalaryId ? parseInt(r.partialSalaryId) : null,
      isActive: r.isActive ?? true,
    })),
    ...(isAdd && { createdById: parseInt(user?.id) }),
    updatedById: parseInt(user?.id),
  });

  return {
    loading,
    dropdownsLoading,
    requestLoading,
    data,
    options,
    setIsGenerate,
    fetchData,
    handleAdd,
    handleUpdate,
    handleFilter,
  };
};